// src/MetadataTranslationModal.tsx
// NOTE: Update frontend/src/__tests__/MetadataTranslationModal.test.tsx and run
// `../scripts/test-frontend.sh MetadataTranslationModal` when editing this modal.
import React from 'react'
import type { TourProps } from 'antd'
import {
  Modal,
  Button,
  Select,
  Checkbox,
  Tooltip,
  Tag,
  Alert,
  Input,
} from 'antd'
import {
  DownOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import { 
  isLanguageBeta,
  getQuotationStyle,
  hasQuotationOptions,
  getDefaultQuotationStyle,
  getQuotationTooltip 
} from './LanguageConfig'
import AIModelSelect from './AIModelSelect';
import type { FrenchNarrativeTone } from './types';

import InsufficientCreditsAlert from './components/InsufficientCreditsAlert';
import ModalInlineNotice from './components/ModalInlineNotice';
import FrenchNarrationInfo, { FrenchNarrationTooltipContent } from './components/FrenchNarrationInfo';
import { useSelectedAIModel } from './hooks/useSelectedAIModel';
import RecommendedTag from './components/RecommendedTag'
import ModalCostBreakdown, { type ModalCostBreakdownRow } from './components/ModalCostBreakdown';
import type { CreditTopUpContext } from './types/creditTopUp';
import { isTutorialModeRequested, isTutorialSandboxModeEnabled } from './utils/tutorialMode';
import { resolveFrontendApiBase } from './utils/apiBase';
import { MAX_BLURB_TRANSLATION_WORDS } from './utils/metadataFreeRuns';
import AppTour, { AppTourVideoLink, renderAppTourPanel } from './tours/AppTour';
import { tourTarget, useGuidedTour } from './tours/useGuidedTour';
import { tutorialVideoLinks } from './tutorialVideoLinks';

export type MetadataTranslationBetaFeedbackPayload = {
  source: 'metadata_translation_modal_beta';
  targetLanguage: string;
  issueType: 'quality' | 'style' | 'formatting' | 'terminology' | 'other';
  feedback: string;
  contactEmail?: string;
  includeContext: boolean;
  selectedAIModel: string;
  quotationStyle: string;
  translateMetadata: {
    title: boolean;
    series: boolean;
    blurb: boolean;
  };
  frenchTone?: string;
}

interface MetadataTranslationModalProps {
  isVisible: boolean
  onCancel: () => void
  onTranslate: () => void
  translateMetadata: {
    title: boolean
    series: boolean
    blurb: boolean
  }
  setTranslateMetadata: React.Dispatch<
    React.SetStateAction<{
      title: boolean
      series: boolean
      blurb: boolean
    }>
  >
  bookLanguage: string
  targetLanguage: string
  setTargetLanguage: React.Dispatch<React.SetStateAction<string>>
  quotationStyle: string
  setQuotationStyle: React.Dispatch<React.SetStateAction<string>>
  selectedAIModel: string
  setSelectedAIModel: React.Dispatch<React.SetStateAction<string>>
  aiInstructions: string
  setAiInstructions: React.Dispatch<React.SetStateAction<string>>
  languages: string[]
  canTranslateTitle: boolean
  canTranslateSeries: boolean
  canTranslateBlurb: boolean
  metadataHasTranslation: (
    type: 'title' | 'series' | 'blurb',
    sourceLang: string,
    targetLang: string,
  ) => boolean
  calculateMetadataTranslationCreditCost: () => number
  metadataOptionFreeRunConsumed?: (
    field: 'title' | 'series',
    sourceLang?: string,
    targetLang?: string,
  ) => boolean
  metadataBlurbFreeRunConsumed?: (targetLang?: string) => boolean
  metadataBlurbCreditCost?: number
  userHasEnoughCreditsMetadata: boolean
  creditBalance: any
  isAnyMetadataSelectedForTranslation: () => boolean
  frenchTone: string
  setFrenchTone: React.Dispatch<React.SetStateAction<string>>
  bookFrenchTone?: FrenchNarrativeTone
  onTopUpCredits?: (context: CreditTopUpContext) => void
  onSubmitBetaFeedback?: (payload: MetadataTranslationBetaFeedbackPayload) => Promise<void> | void
  betaFeedbackEmail?: string
  simpleMode?: boolean
  tourUserId?: string
}

const AUTO_FRENCH_TONE_VALUE = 'AI Detect'
const API_BASE = resolveFrontendApiBase();

const MetadataTranslationModal: React.FC<MetadataTranslationModalProps> = ({
  isVisible,
  onCancel,
  onTranslate,
  translateMetadata,
  setTranslateMetadata,
  bookLanguage,
  targetLanguage,
  setTargetLanguage,
  quotationStyle,
  setQuotationStyle,
  selectedAIModel,
  setSelectedAIModel,
  aiInstructions,
  setAiInstructions,
  languages,
  canTranslateTitle,
  canTranslateSeries,
  canTranslateBlurb,
  metadataHasTranslation,
  calculateMetadataTranslationCreditCost,
  metadataOptionFreeRunConsumed,
  metadataBlurbFreeRunConsumed,
  metadataBlurbCreditCost,
  userHasEnoughCreditsMetadata,
  creditBalance,
  isAnyMetadataSelectedForTranslation,
  frenchTone,
  setFrenchTone,
  bookFrenchTone,
  onTopUpCredits,
  onSubmitBetaFeedback,
  betaFeedbackEmail = '',
  simpleMode = false,
  tourUserId,
}) => {
  const tutorialModeRequested = React.useMemo(
    () => isTutorialModeRequested() || isTutorialSandboxModeEnabled(),
    []
  )
  const selectedModelData = useSelectedAIModel(selectedAIModel);
  const [isCostBreakdownModalVisible, setIsCostBreakdownModalVisible] = React.useState(false)
  const [isBetaFeedbackModalVisible, setIsBetaFeedbackModalVisible] = React.useState(false)
  const [betaFeedbackIssueType, setBetaFeedbackIssueType] = React.useState<MetadataTranslationBetaFeedbackPayload['issueType']>('quality')
  const [betaFeedbackText, setBetaFeedbackText] = React.useState('')
  const [betaFeedbackContactEmail, setBetaFeedbackContactEmail] = React.useState(betaFeedbackEmail)
  const [betaFeedbackIncludeContext, setBetaFeedbackIncludeContext] = React.useState(true)
  const [betaFeedbackSubmitting, setBetaFeedbackSubmitting] = React.useState(false)
  const [betaFeedbackError, setBetaFeedbackError] = React.useState<string | null>(null)
  const setMetadataFieldSelected = React.useCallback(
    (field: keyof typeof translateMetadata, selected: boolean) => {
      setTranslateMetadata({ ...translateMetadata, [field]: selected })
    },
    [setTranslateMetadata, translateMetadata],
  )
  const toggleMetadataFieldSelected = React.useCallback(
    (field: keyof typeof translateMetadata, enabled: boolean) => {
      if (!enabled) return
      setMetadataFieldSelected(field, !translateMetadata[field])
    },
    [setMetadataFieldSelected, translateMetadata],
  )
  const handleMetadataFieldTileClick = React.useCallback(
    (
      event: React.MouseEvent<HTMLElement>,
      field: keyof typeof translateMetadata,
      enabled: boolean,
    ) => {
      const target = event.target as HTMLElement | null
      if (target?.closest('.ant-checkbox-wrapper')) return
      toggleMetadataFieldSelected(field, enabled)
    },
    [toggleMetadataFieldSelected],
  )
  const handleMetadataFieldTileKeyDown = React.useCallback(
    (
      event: React.KeyboardEvent<HTMLElement>,
      field: keyof typeof translateMetadata,
      enabled: boolean,
    ) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      toggleMetadataFieldSelected(field, enabled)
    },
    [toggleMetadataFieldSelected],
  )
  const normalizedBookFrenchTone = React.useMemo(() => {
    const raw = (bookFrenchTone || '').trim().toLowerCase()
    if (!raw) return ''
    if (raw.startsWith('modern')) return 'modern'
    if (raw.startsWith('classical')) return 'classical'
    if (raw.startsWith('present')) return 'present'
    return raw
  }, [bookFrenchTone])
  const isFrenchTarget = targetLanguage === 'French'
  const recommendedFrenchToneValue = normalizedBookFrenchTone || AUTO_FRENCH_TONE_VALUE
  const frenchToneSummary = React.useMemo<React.ReactNode>(() => {
    if (!isFrenchTarget) return ''
    if (!frenchTone) {
      return 'French translations need a narrative voice, so pick the option that fits this book (this setting disappears for other languages).'
    }
    const isUsingSavedTone = normalizedBookFrenchTone && frenchTone === normalizedBookFrenchTone
    const intro = 'French translations need a narrative voice.'
    const savedSuffix = isUsingSavedTone ? ' We already detected and saved this voice for your book, so it will stay consistent.' : ''
    const summaryMap: Record<string, React.ReactNode> = {
      [AUTO_FRENCH_TONE_VALUE]: (
        <>
          {intro} We'll let <strong>AI Detect</strong> whether this metadata needs a <strong>Modern/Accessible</strong> or <strong>Classical/Literary</strong> voice (unique to French) and keep the tense consistent.
        </>
      ),
      modern: (
        <>
          {intro} We'll translate your metadata using a <strong>Modern/Accessible Past Tense</strong> voice.
        </>
      ),
      classical: (
        <>
          {intro} We'll translate your metadata using a <strong>Classical/Literary Past Tense</strong> voice.
        </>
      ),
      present: (
        <>
          {intro} We'll translate your metadata using a consistent <strong>Present Tense</strong> narration.
        </>
      ),
    }
    const node = summaryMap[frenchTone] || frenchTone
    return (
      <>
        {node}
        {savedSuffix}
      </>
    )
  }, [isFrenchTarget, frenchTone, normalizedBookFrenchTone])
  
  const getModelDiscountPercent = React.useCallback(() => {
    const value = Number(selectedModelData?.discount || 0)
    return Math.max(0, value)
  }, [selectedModelData?.discount])

  const getModelPremiumPercent = React.useCallback(() => {
    const value = Number(selectedModelData?.premium || 0)
    return Math.max(0, value)
  }, [selectedModelData?.premium])

  const getNetModelAdjustmentPercent = React.useCallback(() => {
    return getModelPremiumPercent() - getModelDiscountPercent()
  }, [getModelDiscountPercent, getModelPremiumPercent])

  const baseCreditCost = React.useMemo(
    () => calculateMetadataTranslationCreditCost(),
    [calculateMetadataTranslationCreditCost]
  )
  const adjustedCreditCost = React.useMemo(() => {
    const netPct = getNetModelAdjustmentPercent()
    if (!netPct) return baseCreditCost
    return Math.max(0, Math.ceil(baseCreditCost * (1 + netPct / 100)))
  }, [baseCreditCost, getNetModelAdjustmentPercent])
  const availableCredits = React.useMemo(() => {
    const raw = creditBalance?.available_balance ?? creditBalance?.credit_balance
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return raw
    }
    if (typeof raw === 'string') {
      const parsed = Number(raw)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
    return null
  }, [creditBalance])

  // Check if user has enough credits with model pricing adjustment
  const effectiveAvailableCredits = React.useMemo(() => {
    if (typeof availableCredits === 'number') return availableCredits
    if (tutorialModeRequested) return 50000
    return null
  }, [availableCredits, tutorialModeRequested])

  const hasEnoughCreditsForCost =
    typeof effectiveAvailableCredits === 'number' &&
    effectiveAvailableCredits >= adjustedCreditCost
  const metadataShortfallCredits = React.useMemo(() => {
    if (typeof effectiveAvailableCredits !== 'number') return null
    return Math.max(Math.ceil(adjustedCreditCost - effectiveAvailableCredits), 0)
  }, [adjustedCreditCost, effectiveAvailableCredits])
  const handleTopUpCredits = React.useCallback(() => {
    if (!onTopUpCredits || typeof availableCredits !== 'number') return
    const requiredCredits = Math.max(0, Math.ceil(adjustedCreditCost))
    const normalizedAvailable = Math.max(0, Math.floor(availableCredits))
    onTopUpCredits({
      source: 'metadata_modal',
      requiredCredits,
      availableCredits: normalizedAvailable,
      shortfallCredits: Math.max(requiredCredits - normalizedAvailable, 0),
    })
  }, [adjustedCreditCost, availableCredits, onTopUpCredits])

  // Removed legacy $-estimate effect; CostSummary now renders plan-aware estimates

  React.useEffect(() => {
    if (!isVisible) return
    if (!isFrenchTarget) return
    if (!frenchTone) {
      setFrenchTone(normalizedBookFrenchTone || AUTO_FRENCH_TONE_VALUE)
    }
  }, [isVisible, isFrenchTarget, frenchTone, setFrenchTone, normalizedBookFrenchTone])

  React.useEffect(() => {
    if (!isVisible) {
      setIsBetaFeedbackModalVisible(false)
      setBetaFeedbackIssueType('quality')
      setBetaFeedbackText('')
      setBetaFeedbackContactEmail(betaFeedbackEmail)
      setBetaFeedbackIncludeContext(true)
      setBetaFeedbackError(null)
    }
  }, [isVisible, betaFeedbackEmail])

  React.useEffect(() => {
    if (!isBetaFeedbackModalVisible) return
    setBetaFeedbackContactEmail(betaFeedbackEmail)
  }, [betaFeedbackEmail, isBetaFeedbackModalVisible])

  // Handle target language change
  const handleTargetLanguageChange = (value: string) => {
    setTargetLanguage(value)
    // Set default quotation style for the language
    const defaultStyle = getDefaultQuotationStyle(value)
    setQuotationStyle(defaultStyle)
    if (value === 'French' && !frenchTone) {
      setFrenchTone(normalizedBookFrenchTone || AUTO_FRENCH_TONE_VALUE)
    }
  }

  React.useEffect(() => {
    if (!isVisible || !tutorialModeRequested || targetLanguage) {
      return
    }
    const sourceLanguage = (bookLanguage || '').trim().toLowerCase()
    const fallback = languages.find(
      (lang) => Boolean((lang || '').trim()) && (lang || '').trim().toLowerCase() !== sourceLanguage
    )
    if (fallback) {
      handleTargetLanguageChange(fallback)
    }
  }, [bookLanguage, isVisible, languages, targetLanguage, tutorialModeRequested])

  const needsQuotationStyle =
    hasQuotationOptions(targetLanguage) && !quotationStyle
  const needsFrenchTone = !simpleMode && isFrenchTarget && !frenchTone
  const betaLanguageWarning = Boolean(targetLanguage && isLanguageBeta(targetLanguage))

  const closeBetaFeedbackModal = React.useCallback(() => {
    if (betaFeedbackSubmitting) return
    setIsBetaFeedbackModalVisible(false)
    setBetaFeedbackIssueType('quality')
    setBetaFeedbackText('')
    setBetaFeedbackContactEmail(betaFeedbackEmail)
    setBetaFeedbackIncludeContext(true)
    setBetaFeedbackError(null)
  }, [betaFeedbackEmail, betaFeedbackSubmitting])

  const handleSubmitBetaFeedback = React.useCallback(async () => {
    const trimmedFeedback = betaFeedbackText.trim()
    const trimmedEmail = betaFeedbackContactEmail.trim()
    if (trimmedFeedback.length < 10) {
      setBetaFeedbackError('Please share a bit more detail (at least 10 characters).')
      return
    }
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setBetaFeedbackError('Please enter a valid email address or leave it blank.')
      return
    }
    if (!onSubmitBetaFeedback) {
      setBetaFeedbackError('Feedback submission is unavailable right now. Please try again later.')
      return
    }

    setBetaFeedbackSubmitting(true)
    setBetaFeedbackError(null)
    try {
      await onSubmitBetaFeedback({
        source: 'metadata_translation_modal_beta',
        targetLanguage: targetLanguage || '',
        issueType: betaFeedbackIssueType,
        feedback: trimmedFeedback,
        contactEmail: trimmedEmail || undefined,
        includeContext: betaFeedbackIncludeContext,
        selectedAIModel,
        quotationStyle,
        translateMetadata: {
          title: Boolean(translateMetadata.title),
          series: Boolean(translateMetadata.series),
          blurb: Boolean(translateMetadata.blurb),
        },
        frenchTone: frenchTone || undefined,
      })
      closeBetaFeedbackModal()
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.message || 'Unable to submit feedback right now.'
      setBetaFeedbackError(detail)
    } finally {
      setBetaFeedbackSubmitting(false)
    }
  }, [
    betaFeedbackContactEmail,
    betaFeedbackIssueType,
    betaFeedbackText,
    betaFeedbackIncludeContext,
    closeBetaFeedbackModal,
    frenchTone,
    onSubmitBetaFeedback,
    quotationStyle,
    selectedAIModel,
    targetLanguage,
    translateMetadata.blurb,
    translateMetadata.series,
    translateMetadata.title,
  ])

  /** Small "BETA" badge */
  const BetaTag = (): JSX.Element => (
    <Tag
      color="orange"
      style={{ marginLeft: 8, fontSize: '0.75em', padding: '0 4px', lineHeight: 1 }}
    >
      BETA
    </Tag>
  )

  const hasMetadataSelection = React.useMemo(() => {
    if (typeof isAnyMetadataSelectedForTranslation === 'function') {
      const result = isAnyMetadataSelectedForTranslation()
      if (typeof result === 'boolean') {
        return result
      }
    }
    return Boolean(translateMetadata.title || translateMetadata.series || translateMetadata.blurb)
  }, [
    isAnyMetadataSelectedForTranslation,
    translateMetadata.title,
    translateMetadata.series,
    translateMetadata.blurb,
  ])

  const formatCredits = React.useCallback((value: number) => {
    return new Intl.NumberFormat('en-US').format(Math.max(0, Math.round(value)))
  }, [])

  const metadataBlurbWordCount = React.useMemo(() => {
    if (typeof metadataBlurbCreditCost === 'number' && Number.isFinite(metadataBlurbCreditCost)) {
      return Math.max(0, Math.ceil(metadataBlurbCreditCost))
    }
    return 0
  }, [metadataBlurbCreditCost])
  const blurbTooLong = Boolean(translateMetadata.blurb && metadataBlurbWordCount > MAX_BLURB_TRANSLATION_WORDS)
  const disableTranslateButton =
    !hasMetadataSelection ||
    (!tutorialModeRequested && !hasEnoughCreditsForCost) ||
    !targetLanguage ||
    needsQuotationStyle ||
    needsFrenchTone ||
    blurbTooLong
  const hasTitleOrSeriesSelection = Boolean(translateMetadata.title || translateMetadata.series)

  const modelAdjustmentNode = (() => {
    const modelDiscountPct = getModelDiscountPercent()
    const modelPremiumPct = getModelPremiumPercent()
    if (!modelDiscountPct && !modelPremiumPct) return null
    return (
      <>
        {modelDiscountPct > 0 && (
          <Tag className="modal-token-tag modal-token-tag--success modal-token-tag--compact">
            {modelDiscountPct}% OFF
          </Tag>
        )}
        {modelPremiumPct > 0 && (
          <Tag className="modal-token-tag modal-token-tag--warning modal-token-tag--compact">
            +{modelPremiumPct}% Premium
          </Tag>
        )}
      </>
    )
  })()

  const metadataCostRows = React.useMemo<ModalCostBreakdownRow[]>(() => {
    const rows: ModalCostBreakdownRow[] = []
    const titleRegeneration = Boolean(metadataOptionFreeRunConsumed?.('title', bookLanguage, targetLanguage))
    const seriesRegeneration = Boolean(metadataOptionFreeRunConsumed?.('series', bookLanguage, targetLanguage))
    const blurbRegeneration = metadataBlurbFreeRunConsumed ? Boolean(metadataBlurbFreeRunConsumed(targetLanguage)) : true
    const normalizedBlurbCost = (() => {
      if (typeof metadataBlurbCreditCost === 'number' && Number.isFinite(metadataBlurbCreditCost)) {
        return blurbRegeneration ? Math.max(0, Math.ceil(metadataBlurbCreditCost)) : 0
      }
      const titleCost = translateMetadata.title && titleRegeneration ? 50 : 0
      const seriesCost = translateMetadata.series && seriesRegeneration ? 50 : 0
      return Math.max(0, Math.ceil(baseCreditCost - titleCost - seriesCost))
    })()

    if (translateMetadata.title) {
      rows.push({
        key: 'metadata-title',
        label: 'Title',
        amountCredits: titleRegeneration ? 50 : 0,
        displayValue: `${formatCredits(titleRegeneration ? 50 : 0)} credits`,
        explanation: titleRegeneration
          ? 'Regenerating options'
          : 'First successful run for this language is included',
        tone: titleRegeneration ? 'warning' : 'success',
      })
    }
    if (translateMetadata.series) {
      rows.push({
        key: 'metadata-series',
        label: 'Series Name',
        amountCredits: seriesRegeneration ? 50 : 0,
        displayValue: `${formatCredits(seriesRegeneration ? 50 : 0)} credits`,
        explanation: seriesRegeneration
          ? 'Regenerating options'
          : 'First successful run for this language is included',
        tone: seriesRegeneration ? 'warning' : 'success',
      })
    }
    if (translateMetadata.blurb) {
      rows.push({
        key: 'metadata-blurb',
        label: 'Blurb',
        amountCredits: normalizedBlurbCost,
        displayValue: blurbRegeneration ? `${formatCredits(normalizedBlurbCost)} credits` : 'Included',
        explanation: blurbRegeneration
          ? 'Charged by word count'
          : 'First blurb translation for this language is free',
        tone: blurbRegeneration ? 'warning' : 'success',
      })
    }
    const modelDiscountPct = getModelDiscountPercent()
    const modelPremiumPct = getModelPremiumPercent()
    if (modelDiscountPct > 0) {
      rows.push({
        key: 'metadata-model-discount',
        label: 'Model discount',
        displayValue: `-${modelDiscountPct}%`,
        tone: 'success',
      })
    }
    if (modelPremiumPct > 0) {
      rows.push({
        key: 'metadata-model-premium',
        label: 'Model premium',
        displayValue: `+${modelPremiumPct}%`,
        tone: 'warning',
      })
    }
    return rows
  }, [
    baseCreditCost,
    bookLanguage,
    formatCredits,
    getModelDiscountPercent,
    getModelPremiumPercent,
    metadataBlurbCreditCost,
    metadataBlurbFreeRunConsumed,
    metadataOptionFreeRunConsumed,
    targetLanguage,
    translateMetadata.blurb,
    translateMetadata.series,
    translateMetadata.title,
  ])

  const metadataTour = useGuidedTour({
    userId: tourUserId,
    apiBase: API_BASE,
    flag: 'translateMetadataModalTourSeen',
    enabled: isVisible,
    autoStart: true,
  })
  const metadataTourSteps = React.useMemo<TourProps['steps']>(() => ([
    {
      title: 'Choose metadata translation setup',
      description: renderAppTourPanel('Confirm the source language, target language, model, and language-specific style settings.'),
      target: tourTarget('translate-metadata-modal-setup'),
    },
    {
      title: 'Pick metadata fields',
      description: renderAppTourPanel(
        <>
          Select Title, Series Name, Blurb, or any combination that should be translated or replaced.
          <AppTourVideoLink href={tutorialVideoLinks.translatedDashboard}>
            Watch the translated dashboard tutorial
          </AppTourVideoLink>
        </>
      ),
      target: tourTarget('translate-metadata-modal-fields', 'translate-metadata-modal-setup'),
    },
    {
      title: 'Add optional guidance',
      description: renderAppTourPanel('Add short phrasing or tone guidance for Title and Series Name translations, then resolve any blockers.'),
      target: tourTarget('translate-metadata-modal-readiness', 'translate-metadata-modal-fields'),
    },
    {
      title: 'Review credits',
      description: renderAppTourPanel('The cost breakdown explains which metadata is included, which fields cost credits, and your remaining balance.'),
      target: tourTarget('translate-metadata-modal-cost', 'translate-metadata-modal-readiness'),
    },
    {
      title: 'Start metadata translation',
      description: renderAppTourPanel('When every requirement is ready, start translation from the footer.'),
      target: tourTarget('translate-metadata-modal-submit', 'translate-metadata-modal-cost'),
    },
  ]), [])

  const footerCostSummary = (
    <div className="translation-modal-footer-summary" aria-live="polite" data-testid="tutorial:translate-metadata:cost-summary">
      {hasMetadataSelection ? (
        <span>
          <Tooltip title="Title, Series Name, and the first Blurb translation for each language are included. Later regenerations may cost credits. Blurbs are capped at 1,000 words.">
            <span>
              <strong>{formatCredits(adjustedCreditCost)} credits</strong> required
              <QuestionCircleOutlined className="modal-token-icon metadata-translation-modal-inline-icon" />
            </span>
          </Tooltip>
        </span>
      ) : (
        <span>Select metadata fields to calculate cost</span>
      )}
      <span className="translation-modal-footer-summary__separator">·</span>
      <span>
        <strong>
          {typeof effectiveAvailableCredits === 'number' ? formatCredits(effectiveAvailableCredits) : 'Loading...'}
        </strong>{' '}
        available
      </span>
      {!hasEnoughCreditsForCost && !tutorialModeRequested ? (
        <>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span className="translation-modal-footer-summary__warning">Top-up needed</span>
        </>
      ) : null}
      {hasMetadataSelection && modelAdjustmentNode ? (
        <span className="metadata-translation-modal-footer-tags">{modelAdjustmentNode}</span>
      ) : null}
      <Button
        type="link"
        size="small"
        className="translation-modal-footer-summary__details"
        aria-label="View cost breakdown"
        onClick={() => setIsCostBreakdownModalVisible(true)}
        data-tour-id="translate-metadata-modal-cost"
      >
        View details
      </Button>
    </div>
  )

  return (
    <>
    <Modal
      title={
        <div className="translation-modal-titlebar">
          <span className="translation-modal-titlebar__title">Translate Metadata</span>
          <Button
            type="text"
            size="small"
            icon={<QuestionCircleOutlined />}
            onClick={metadataTour.start}
            data-tour-id="translate-metadata-modal-tour-trigger"
            className="translation-modal-titlebar__tour app-tour-trigger"
          >
            Take a tour
          </Button>
        </div>
      }
      visible={isVisible}
      onCancel={onCancel}
      centered
      className="translation-modal-footer-stable metadata-translation-modal"
      width="min(820px, calc(100vw - 32px))"
      bodyStyle={{
        maxHeight: 'min(76vh, calc(100vh - 220px))',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '16px 16px 12px',
      }}
      footer={[
        <React.Fragment key="cost-summary">
          {footerCostSummary}
        </React.Fragment>,
        <Button key="cancel" onClick={onCancel} data-testid="tutorial:translate-metadata:cancel-button">
          Cancel
        </Button>,
        <Button
          key="translate"
          type="primary"
          className="app-primary-cta"
          onClick={() => onTranslate()}
          data-testid="tutorial:translate-metadata:translate-button"
          data-tour-id="translate-metadata-modal-submit"
          disabled={disableTranslateButton}
        >
          Translate Metadata
        </Button>,
      ]}
    >
      <div className="translation-modal-body metadata-translation-modal-body" data-testid="tutorial:translate-metadata:modal">
        <section
          className="translation-modal-block translation-modal-block--scope"
          aria-labelledby="metadata-translation-setup-heading"
          data-tour-id="translate-metadata-modal-setup"
          data-testid="tutorial:translate-metadata:setup"
        >
          <div className="translation-modal-block-header">
            <div>
              <h3 id="metadata-translation-setup-heading">Translation setup</h3>
              <p>Choose the language, model, and style settings for this metadata translation.</p>
            </div>
          </div>
          <div className="metadata-translation-modal-setup-stack">
            <div className="metadata-translation-modal-language-row">
              <div className="metadata-translation-modal-setup-cell metadata-translation-modal-setup-cell--readonly">
                <span className="translation-modal-kicker">Source language</span>
                <strong>{bookLanguage || 'Not selected'}</strong>
              </div>
              <div className="metadata-translation-modal-language-divider" aria-hidden="true">
                to
              </div>
              <div className="metadata-translation-modal-setup-cell">
                <span className="translation-modal-kicker">Target language</span>
                <div data-testid="tutorial:translate-metadata:target-language">
                  <Select
                    value={targetLanguage}
                    onChange={handleTargetLanguageChange}
                    style={{ width: '100%' }}
                    placeholder="Select a target language"
                    suffixIcon={
                      <span className="ant-select-suffix">
                        <DownOutlined className="modal-token-icon" />
                      </span>
                    }
                  >
                    {languages
                      .filter((lang) => lang !== bookLanguage)
                      .map((lang) => {
                        const optionSlug = String(lang || '')
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, '-')
                          .replace(/^-+|-+$/g, '')
                        return (
                          <Select.Option key={lang} value={lang}>
                            {optionSlug === 'german' ? (
                              <span data-testid="tutorial:translate-metadata:target-language-option:german">
                                {lang}
                                {isLanguageBeta(lang) && <BetaTag />}
                              </span>
                            ) : (
                              <span data-testid={`tutorial:translate-metadata:target-language-option:${optionSlug}`}>
                                {lang}
                                {isLanguageBeta(lang) && <BetaTag />}
                              </span>
                            )}
                          </Select.Option>
                        )
                    })}
                  </Select>
                </div>
                {betaLanguageWarning && targetLanguage && (
                  <div className="metadata-translation-modal-setup-helper">
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
                      <strong>{targetLanguage} translation is currently in beta.</strong>
                      <Button
                        type="link"
                        size="small"
                        style={{ paddingInline: 0, height: 'auto', fontSize: 12 }}
                        onClick={() => {
                          setBetaFeedbackError(null)
                          setIsBetaFeedbackModalVisible(true)
                        }}
                      >
                        Give feedback
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="metadata-translation-modal-settings-row">
              {!simpleMode && (
                <div className="metadata-translation-modal-setup-cell">
                  <span className="translation-modal-kicker">AI model</span>
                  <div data-testid="tutorial:translate-metadata:ai-model">
                    <AIModelSelect
                      workflow="metadata"
                      value={selectedAIModel}
                      onChange={setSelectedAIModel}
                      language={targetLanguage}
                      sourceLanguage={bookLanguage}
                      isLangTesterUser={false} // Metadata translation doesn't need testing models
                      autoSelect={false}
                    />
                  </div>
                </div>
              )}
              <div className="metadata-translation-modal-setup-cell">
                <span className="translation-modal-kicker">
                  Quotation style
                  <Tooltip title={getQuotationTooltip(targetLanguage)}>
                    <QuestionCircleOutlined
                      className="modal-token-icon modal-token-icon--interactive"
                    />
                  </Tooltip>
                  {needsQuotationStyle && (
                    <Tag className="modal-token-tag modal-token-tag--danger">
                      Required
                    </Tag>
                  )}
                </span>
                {(() => {
                  const quotationConfig = getQuotationStyle(targetLanguage)
                  if (!quotationConfig) {
                    return (
                      <div data-testid="tutorial:translate-metadata:quotation-style">
                        <Select disabled value="unknown" style={{ width: '100%' }}>
                          <Select.Option value="unknown"> </Select.Option>
                        </Select>
                        <div className="metadata-translation-modal-setup-helper"></div>
                      </div>
                    )
                  }
                  if (quotationConfig.options) {
                    return (
                      <div data-testid="tutorial:translate-metadata:quotation-style">
                        <Select
                          value={quotationStyle}
                          onChange={setQuotationStyle}
                          placeholder="Please select a quotation style"
                          style={{ width: '100%' }}
                          status={needsQuotationStyle ? 'error' : undefined}
                          suffixIcon={
                            <span className="ant-select-suffix">
                              <DownOutlined className="modal-token-icon" />
                            </span>
                          }
                        >
                          {quotationConfig.options.map(option => (
                            <Select.Option key={option.value} value={option.value}>
                              {option.label} ({option.marks})
                              {option.isDefault && (
                                <RecommendedTag style={{ position: 'relative', top: '-0.5px' }} />
                              )}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                    )
                  }
                  if (quotationConfig.fixed) {
                    return (
                      <div data-testid="tutorial:translate-metadata:quotation-style">
                        <Select disabled value="fixed" style={{ width: '100%' }}>
                          <Select.Option value="fixed">
                            {quotationConfig.fixed.name} ({quotationConfig.fixed.marks})
                          </Select.Option>
                        </Select>
                      </div>
                    )
                  }
                })()}
              </div>
              {!simpleMode && isFrenchTarget && (
                <div className="metadata-translation-modal-setup-cell metadata-translation-modal-setup-cell--wide">
                  <span className="translation-modal-kicker">
                    Narrative style
                    <Tooltip
                      styles={{
                        root: { maxWidth: 520 },
                        body: { maxWidth: 520 },
                      }}
                      title={<FrenchNarrationTooltipContent />}
                    >
                      <QuestionCircleOutlined className="modal-token-icon modal-token-icon--interactive" />
                    </Tooltip>
                    <FrenchNarrationInfo linkStyle={{ marginLeft: 8 }} variant="simple" />
                    {!frenchTone && (
                      <Tag className="modal-token-tag modal-token-tag--danger">
                        Required
                      </Tag>
                    )}
                  </span>
                  <Select
                    value={frenchTone || undefined}
                    onChange={(value) => setFrenchTone(value || '')}
                    placeholder="Select a narrative voice"
                    style={{ width: '100%' }}
                    status={needsFrenchTone ? 'error' : undefined}
                    allowClear
                    suffixIcon={
                      <span className="ant-select-suffix">
                        <DownOutlined className="modal-token-icon" />
                      </span>
                    }
                  >
                    <Select.Option value={AUTO_FRENCH_TONE_VALUE}>
                      AI Detect (recommend voice & tense)
                      {recommendedFrenchToneValue === AUTO_FRENCH_TONE_VALUE && <RecommendedTag />}
                    </Select.Option>
                    <Select.Option value="modern">
                      Modern/Accessible voice
                      {recommendedFrenchToneValue === 'modern' && <RecommendedTag />}
                    </Select.Option>
                    <Select.Option value="classical">
                      Classical/Literary voice
                      {recommendedFrenchToneValue === 'classical' && <RecommendedTag />}
                    </Select.Option>
                    <Select.Option value="present">
                      Present Tense (source narration mainly present)
                      {recommendedFrenchToneValue === 'present' && <RecommendedTag />}
                    </Select.Option>
                  </Select>
                  {frenchToneSummary && (
                    <div className="metadata-translation-modal-setup-helper metadata-translation-modal-setup-helper--accent">
                      {frenchToneSummary}
                    </div>
                  )}
                  {needsFrenchTone && (
                    <div className="metadata-translation-modal-setup-helper metadata-translation-modal-setup-helper--error">
                      Narrative voice is required for French metadata translation.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        <section
          className="translation-modal-block"
          aria-labelledby="metadata-translation-fields-heading"
          data-tour-id="translate-metadata-modal-fields"
          data-testid="tutorial:translate-metadata:fields"
        >
          <div className="translation-modal-block-header">
            <div>
              <h3 id="metadata-translation-fields-heading">Metadata fields</h3>
              <p>Select which metadata fields should be translated or replaced.</p>
            </div>
          </div>
          <div className="metadata-translation-modal-field-row">
            <span
              className={!canTranslateTitle ? 'metadata-translation-modal-field-option metadata-translation-modal-field-option--disabled' : 'metadata-translation-modal-field-option'}
              data-testid="tutorial:translate-metadata:field-title"
              role="checkbox"
              aria-checked={translateMetadata.title}
              aria-disabled={!canTranslateTitle}
              tabIndex={canTranslateTitle ? 0 : -1}
              onClick={(event) => handleMetadataFieldTileClick(event, 'title', canTranslateTitle)}
              onKeyDown={(event) => handleMetadataFieldTileKeyDown(event, 'title', canTranslateTitle)}
            >
              <Checkbox
                checked={translateMetadata.title}
                onChange={(e) =>
                  setMetadataFieldSelected('title', e.target.checked)
                }
                disabled={!canTranslateTitle}
              >
                <span style={{ fontWeight: 500 }}>Title</span>
                {translateMetadata.title &&
                  metadataHasTranslation('title', bookLanguage, targetLanguage) && (
                    <Tooltip title="A translation already exists for this language and will be replaced.">
                      <ExclamationCircleOutlined className="metadata-translation-modal-warning-icon" />
                    </Tooltip>
                  )}
                {!canTranslateTitle && (
                  <Tooltip title="Add and save a title to enable this option.">
                    <ExclamationCircleOutlined className="metadata-translation-modal-warning-icon" />
                  </Tooltip>
                )}
              </Checkbox>
              {!canTranslateTitle && (
                <div className="metadata-translation-modal-field-helper">Add and save a title first.</div>
              )}
            </span>

            <span
              className={!canTranslateSeries ? 'metadata-translation-modal-field-option metadata-translation-modal-field-option--disabled' : 'metadata-translation-modal-field-option'}
              data-testid="tutorial:translate-metadata:field-series"
              role="checkbox"
              aria-checked={translateMetadata.series}
              aria-disabled={!canTranslateSeries}
              tabIndex={canTranslateSeries ? 0 : -1}
              onClick={(event) => handleMetadataFieldTileClick(event, 'series', canTranslateSeries)}
              onKeyDown={(event) => handleMetadataFieldTileKeyDown(event, 'series', canTranslateSeries)}
            >
              <Checkbox
                checked={translateMetadata.series}
                onChange={(e) =>
                  setMetadataFieldSelected('series', e.target.checked)
                }
                disabled={!canTranslateSeries}
              >
                <span style={{ fontWeight: 500 }}>Series Name</span>
                {translateMetadata.series &&
                  metadataHasTranslation('series', bookLanguage, targetLanguage) && (
                    <Tooltip title="A translation already exists for this language and will be replaced.">
                      <ExclamationCircleOutlined className="metadata-translation-modal-warning-icon" />
                    </Tooltip>
                  )}
                {!canTranslateSeries && (
                  <Tooltip title="Add and save a series name to enable this option.">
                    <ExclamationCircleOutlined className="metadata-translation-modal-warning-icon" />
                  </Tooltip>
                )}
              </Checkbox>
              {!canTranslateSeries && (
                <div className="metadata-translation-modal-field-helper">Add and save a series name first.</div>
              )}
            </span>

            <span
              className={!canTranslateBlurb ? 'metadata-translation-modal-field-option metadata-translation-modal-field-option--disabled' : 'metadata-translation-modal-field-option'}
              data-testid="tutorial:translate-metadata:field-blurb"
              role="checkbox"
              aria-checked={translateMetadata.blurb}
              aria-disabled={!canTranslateBlurb}
              tabIndex={canTranslateBlurb ? 0 : -1}
              onClick={(event) => handleMetadataFieldTileClick(event, 'blurb', canTranslateBlurb)}
              onKeyDown={(event) => handleMetadataFieldTileKeyDown(event, 'blurb', canTranslateBlurb)}
            >
              <Checkbox
                checked={translateMetadata.blurb}
                onChange={(e) =>
                  setMetadataFieldSelected('blurb', e.target.checked)
                }
                disabled={!canTranslateBlurb}
              >
                <span style={{ fontWeight: 500 }}>Blurb</span>
                {translateMetadata.blurb &&
                  metadataHasTranslation('blurb', bookLanguage, targetLanguage) && (
                    <Tooltip title="A translation already exists for this language and will be replaced.">
                      <ExclamationCircleOutlined className="metadata-translation-modal-warning-icon" />
                    </Tooltip>
                  )}
                {!canTranslateBlurb && (
                  <Tooltip title="Add and save a blurb to enable this option.">
                    <ExclamationCircleOutlined className="metadata-translation-modal-warning-icon" />
                  </Tooltip>
                )}
              </Checkbox>
              {!canTranslateBlurb && (
                <div className="metadata-translation-modal-field-helper">Add and save a blurb first.</div>
              )}
            </span>
          </div>
        </section>

        <section
          className="translation-modal-block"
          aria-labelledby="metadata-translation-readiness-heading"
          data-tour-id="translate-metadata-modal-readiness"
          data-testid="tutorial:translate-metadata:readiness"
        >
          <div className="translation-modal-block-header">
            <div>
              <h3 id="metadata-translation-readiness-heading">Guidance and readiness</h3>
              <p>Add optional guidance and resolve any blocking requirements before translating.</p>
            </div>
          </div>
          <div className="translation-readiness-panels">
            {!simpleMode && (
            <div className={`translation-readiness-panel${hasTitleOrSeriesSelection ? '' : ' translation-readiness-panel--empty'}`}>
              <div
                data-testid="tutorial:translate-metadata:optional-instructions"
                className="metadata-translation-modal-instructions"
              >
                <div className="metadata-translation-modal-instructions-header">
                  <div>
                    <div className="translation-modal-subheading">Additional instructions</div>
                    {hasTitleOrSeriesSelection ? (
                      <div className="metadata-translation-modal-instructions-scope">
                        <Tooltip title="These notes help the AI choose phrasing or tone for Title and Series Name translations. Examples: 'Keep it dark and gritty', 'Favor literal translation', 'Avoid slang'.">
                          <QuestionCircleOutlined />
                        </Tooltip>
                        <span>Applies to Title and Series Name. Description and Blurb translations ignore these notes.</span>
                      </div>
                    ) : (
                      <div className="metadata-translation-modal-instructions-scope metadata-translation-modal-instructions-scope--locked">
                        Available after selecting Title or Series.
                      </div>
                    )}
                  </div>
                  {hasTitleOrSeriesSelection && <span>{aiInstructions.length}/300 characters</span>}
                </div>
          {hasTitleOrSeriesSelection ? (
            <>
              <div className="metadata-translation-modal-instructions-input">
                <Input.TextArea
                  data-testid="tutorial:translate-metadata:optional-instructions-input"
                  value={aiInstructions}
                  onChange={(e) => setAiInstructions(e.target.value.slice(0, 300))}
                  maxLength={300}
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  placeholder="Example: Keep the title literal, use Dutch marketplace phrasing, preserve genre tone."
                />
              </div>
            </>
          ) : (
            <div className="metadata-translation-modal-instructions-locked">
              Select Title or Series above to add phrasing or tone guidance.
            </div>
          )}
              </div>
            </div>
            )}
        {!hasEnoughCreditsForCost && !tutorialModeRequested && (
          <InsufficientCreditsAlert
            message="You do not have enough credits for this translation."
            shortfallCredits={metadataShortfallCredits}
            onTopUpCredits={onTopUpCredits ? handleTopUpCredits : undefined}
            style={{ marginBottom: 8 }}
          />
        )}
        {blurbTooLong && (
          <Alert
            className="modal-alert-compact"
            message={`Blurb must be ${MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words or fewer to translate as metadata.`}
            description={`Current blurb length: ${metadataBlurbWordCount.toLocaleString()} words.`}
            type="error"
            showIcon={false}
            style={{ marginBottom: 8 }}
          />
        )}
        {needsQuotationStyle && (
          <Alert
            className="modal-alert-compact"
            message={`Please select a quotation style for ${targetLanguage} translation.`}
            type="error"
            showIcon={false}
            style={{ marginBottom: 8 }}
          />
        )}
        {needsFrenchTone && (
          <Alert
            className="modal-alert-compact"
            message="Please select a French narrative style before translating."
            type="error"
            showIcon={false}
            style={{ marginBottom: 8 }}
          />
        )}
        {(translateMetadata.title || translateMetadata.series) && (
          <ModalInlineNotice
            message="Tip: Title and Series translations work best when Primary Genre, Tropes, Keywords, and Blurb are populated."
            tone="info"
          />
        )}
          </div>
        </section>
      </div>
    </Modal>
    <Modal
      title="Cost breakdown"
      visible={isCostBreakdownModalVisible}
      onCancel={() => setIsCostBreakdownModalVisible(false)}
      width={560}
      footer={[
        <Button key="close-metadata-cost-breakdown" onClick={() => setIsCostBreakdownModalVisible(false)}>
          Close
        </Button>,
      ]}
    >
      <div data-testid="metadata-cost-breakdown-modal">
        <ModalCostBreakdown
          rows={metadataCostRows}
          totalCredits={hasMetadataSelection ? adjustedCreditCost : null}
          availableCredits={effectiveAvailableCredits}
          emptyMessage="Select metadata fields to calculate cost."
          formatCredits={formatCredits}
        />
      </div>
    </Modal>
    <Modal
      title={
        <div className="translation-modal-titlebar">
          <span className="translation-modal-titlebar__title">Share Translation Beta Feedback</span>
        </div>
      }
      visible={isBetaFeedbackModalVisible}
      onCancel={closeBetaFeedbackModal}
      onOk={() => void handleSubmitBetaFeedback()}
      okText="Submit Feedback"
      confirmLoading={betaFeedbackSubmitting}
      cancelButtonProps={{ disabled: betaFeedbackSubmitting }}
      className="metadata-beta-feedback-modal"
    >
      <div className="translation-modal-body metadata-beta-feedback-body">
        <section className="translation-modal-block">
          <div className="translation-modal-block-header">
            <div>
              <span className="translation-modal-kicker">Topic</span>
              <h3>Feedback category</h3>
              <p>Pick the area that best matches the translation issue.</p>
            </div>
          </div>
          <label className="metadata-beta-feedback-field">
            <span>Feedback Topic</span>
          <Select
            value={betaFeedbackIssueType}
            onChange={(value) => setBetaFeedbackIssueType(value as MetadataTranslationBetaFeedbackPayload['issueType'])}
            className="metadata-beta-feedback-control"
          >
            <Select.Option value="quality">Translation quality</Select.Option>
            <Select.Option value="style">Voice or style</Select.Option>
            <Select.Option value="formatting">Formatting or punctuation</Select.Option>
            <Select.Option value="terminology">Terminology or glossary</Select.Option>
            <Select.Option value="other">Other</Select.Option>
          </Select>
          </label>
        </section>
        <section className="translation-modal-block">
          <div className="translation-modal-block-header">
            <div>
              <span className="translation-modal-kicker">Details</span>
              <h3>What should we improve?</h3>
              <p>Include what you expected and what happened.</p>
            </div>
          </div>
          <label className="metadata-beta-feedback-field">
            <span>Feedback</span>
          <Input.TextArea
            value={betaFeedbackText}
            onChange={(event) => setBetaFeedbackText(event.target.value)}
            rows={5}
            maxLength={2000}
            showCount
            placeholder="Tell us what you expected and what happened."
          />
          </label>
        </section>
        <section className="translation-modal-block">
          <div className="translation-modal-block-header">
            <div>
              <span className="translation-modal-kicker">Follow-up</span>
              <h3>Contact and context</h3>
              <p>Optionally include contact details and current translation settings.</p>
            </div>
          </div>
          <label className="metadata-beta-feedback-field">
            <span>Contact Email (optional)</span>
          <Input
            value={betaFeedbackContactEmail}
            onChange={(event) => setBetaFeedbackContactEmail(event.target.value)}
            placeholder="you@example.com"
          />
          </label>
        <Checkbox
          checked={betaFeedbackIncludeContext}
          onChange={(event) => setBetaFeedbackIncludeContext(Boolean(event.target.checked))}
          className="metadata-beta-feedback-context"
        >
          Include current translation settings with this feedback.
        </Checkbox>
        {betaFeedbackError && (
          <Alert
            className="modal-alert-compact"
            type="error"
            showIcon={false}
            message={betaFeedbackError}
          />
        )}
        </section>
      </div>
    </Modal>
    <AppTour
      open={metadataTour.open}
      steps={metadataTourSteps}
      current={metadataTour.current}
      onChange={metadataTour.setCurrent}
      onClose={metadataTour.close}
      onFinish={metadataTour.close}
      zIndex={2400}
    />
    </>
  )
}

export default MetadataTranslationModal
